/**
 * Mapea pedidos de API a filas de Relación de Despachos
 */
import Ciudades from '../../assets/data/ciudades.json'
import type { ManifiestoDespachoRow } from '../../types/manifiestos'

interface CiudadItem {
  codigo: string
  ciudad: string
}

const ciudadesList = Ciudades as CiudadItem[]

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : {}
}

function str(value: unknown): string {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

function num(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const cleaned = str(value).replace(/,/g, '')
  const parsed = Number(cleaned)
  return Number.isFinite(parsed) ? parsed : 0
}

function formatMoney(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}

/** Resuelve código DANE → nombre de ciudad (ciudades.json) */
function getNombreCiudad(codigoONombre: string): string {
  const raw = str(codigoONombre)
  if (!raw) return ''

  const byCode = ciudadesList.find((c) => c.codigo === raw)
  if (byCode?.ciudad) return byCode.ciudad.toUpperCase()

  const padded = raw.padStart(5, '0')
  const byPadded = ciudadesList.find((c) => c.codigo === padded)
  if (byPadded?.ciudad) return byPadded.ciudad.toUpperCase()

  return raw.toUpperCase()
}

function getDestinatario(order: Record<string, unknown>): string {
  const full = str(order.customer_full_name)
  if (full) return full.toUpperCase()
  const first = str(order.first_name)
  const last = str(order.last_name)
  return `${first} ${last}`.trim().toUpperCase()
}

function getCiudadCodigoRaw(order: Record<string, unknown>): string {
  const shipping = asRecord(order.shippingAddress)
  return (
    str(order.shipping_city) ||
    str(shipping.city) ||
    str(order.billing_city) ||
    str(asRecord(order.billingAddress).city)
  )
}

function getCiudad(order: Record<string, unknown>): string {
  const cityName = getNombreCiudad(getCiudadCodigoRaw(order))
  const shipping = asRecord(order.shippingAddress)
  const stateRaw =
    str(order.shipping_state) ||
    str(shipping.state) ||
    str(asRecord(order.billingAddress).state)

  const stateLooksLikeCode = /^\d+$/.test(stateRaw)
  if (cityName && stateRaw && !stateLooksLikeCode) {
    return `${cityName}-${stateRaw.toUpperCase()}`
  }
  return cityName
}

function getDireccion(order: Record<string, unknown>): string {
  const shipping = asRecord(
    order.shippingAddress || order.shipping || order.shipping_address
  )
  const line1 =
    str(shipping.address_1) ||
    str(shipping.address1) ||
    str(order.shipping_address_1) ||
    str(order.shipping_address)
  const line2 =
    str(shipping.address_2) ||
    str(shipping.address2) ||
    str(order.shipping_address_2)

  const full = [line1, line2].filter(Boolean).join('. ')
  return full.toUpperCase()
}

function getProducts(order: Record<string, unknown>): Record<string, unknown>[] {
  const products =
    order.products || order.line_items || order.lineItems || order.lineitems
  if (!Array.isArray(products)) return []
  return products.map((p) => asRecord(p))
}

/** Contenido = SKUs del pedido (con cantidad si > 1) */
function getContenido(products: Record<string, unknown>[]): string {
  if (products.length === 0) return ''
  return products
    .map((p) => {
      const sku = str(p.sku || p.SKU || p.product_sku)
      if (!sku) return ''
      const qty = num(p.quantity) || 1
      return qty > 1 ? `${sku} x${qty}` : sku
    })
    .filter(Boolean)
    .join(', ')
    .toUpperCase()
    .slice(0, 120)
}

function getUnidades(products: Record<string, unknown>[]): number {
  if (products.length === 0) return 1
  return products.reduce((sum, p) => sum + (num(p.quantity) || 1), 0)
}

/**
 * Peso: suma de (producto.peso × cantidad) desde products/lineItems del pedido.
 * Si el API no trae peso, queda 0.
 */
function getPeso(products: Record<string, unknown>[]): number {
  return products.reduce((sum, p) => {
    const peso = num(p.peso || p.weight)
    const qty = num(p.quantity) || 1
    return sum + peso * qty
  }, 0)
}

/**
 * Volumen: producto.volumen/volume, o (largo×ancho×alto)/4000 × cantidad.
 * Si el API no trae esos campos, queda 0.
 */
function getVolumen(products: Record<string, unknown>[]): number {
  return products.reduce((sum, p) => {
    const vol = num(p.volumen || p.volume)
    const qty = num(p.quantity) || 1
    if (vol > 0) return sum + vol * qty
    const l = num(p.longitud || p.length)
    const a = num(p.ancho || p.width)
    const h = num(p.alto || p.height)
    if (l > 0 && a > 0 && h > 0) {
      return sum + ((l * a * h) / 4000) * qty
    }
    return sum
  }, 0)
}

/** Cédula / documento del cliente destinatario */
function getDocumentoCliente(order: Record<string, unknown>): string {
  const customer = asRecord(order.customer)
  return (
    str(order.document) ||
    str(customer.document) ||
    str(order.customer_document) ||
    str(order.cedula) ||
    str(customer.cedula) ||
    ''
  )
}

function getFlete(order: Record<string, unknown>): number {
  const extra = asRecord(order.extraDetails)
  return num(order.costo_envio ?? extra.costo_envio ?? order.shipping_total)
}

function getDeclarado(order: Record<string, unknown>): number {
  return num(order.total)
}

export interface MapManifiestoResult {
  rows: ManifiestoDespachoRow[]
  skippedWithoutGuia: number
}

/**
 * Convierte pedidos crudos en filas de manifiesto; excluye sin guía
 */
export function mapOrdersToManifiestoRows(
  orders: unknown[]
): MapManifiestoResult {
  const rows: ManifiestoDespachoRow[] = []
  let skippedWithoutGuia = 0
  let index = 0

  orders.forEach((raw) => {
    const order = asRecord(raw)
    const guia = str(order.guia)
    if (!guia) {
      skippedWithoutGuia += 1
      return
    }

    const products = getProducts(order)
    const orderId = num(order.id)
    const ciudad = getCiudad(order)
    index += 1

    rows.push({
      index,
      numeroVisible: String(index),
      orderId,
      destinatario: getDestinatario(order),
      ciudad,
      direccion: getDireccion(order),
      contenido: getContenido(products),
      documento: getDocumentoCliente(order),
      und: getUnidades(products),
      peso: Math.round(getPeso(products)),
      vol: Math.round(getVolumen(products)),
      decl: getDeclarado(order),
      declLabel: formatMoney(getDeclarado(order)),
      flete: getFlete(order),
      fleteLabel: formatMoney(getFlete(order)),
      guia,
      ciudadCentro: ciudad.split('-')[0]?.trim() || 'SIN CIUDAD',
    })
  })

  return { rows, skippedWithoutGuia }
}

export function buildCitySummary(rows: ManifiestoDespachoRow[]) {
  const map = new Map<
    string,
    { ciudad: string; items: number; unds: number; docEntr: number; docDev: number }
  >()

  rows.forEach((row) => {
    const key = row.ciudadCentro
    const current = map.get(key) || {
      ciudad: key,
      items: 0,
      unds: 0,
      docEntr: 0,
      docDev: 0,
    }
    current.items += 1
    current.unds += row.und
    current.docEntr += 1
    map.set(key, current)
  })

  return Array.from(map.values())
}
